import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from "react";
import { useMediaQuery } from "@/lib/hooks";
import {
  SYSTEM_DARK_QUERY,
  applyTheme,
  readStoredThemeChoice,
  resolveTheme,
  storeThemeChoice,
  type ThemeChoice,
  type ThemeId,
} from "./themes";

interface ThemeContextValue {
  /** The stored choice ("system" until the user picks one). */
  choice: ThemeChoice;
  /** Whether the choice came from this device's storage (false: still following the default). */
  chosenOnThisDevice: boolean;
  /** The theme actually applied. */
  theme: ThemeId;
  setChoice: (choice: ThemeChoice) => void;
  /** Adopt a choice made elsewhere (the workspace setting) without marking it as chosen here. */
  adoptChoice: (choice: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Applies the theme to <html>; follows prefers-color-scheme until a theme is chosen. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [stored] = useState(readStoredThemeChoice);
  const [choice, setChoiceState] = useState<ThemeChoice>(stored ?? "system");
  const [chosenOnThisDevice, setChosenOnThisDevice] = useState(stored !== null);
  const systemPrefersDark = useMediaQuery(SYSTEM_DARK_QUERY);
  const theme = resolveTheme(choice, systemPrefersDark);

  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setChoice = useCallback((next: ThemeChoice) => {
    storeThemeChoice(next);
    setChosenOnThisDevice(true);
    setChoiceState(next);
  }, []);

  const adoptChoice = useCallback((next: ThemeChoice) => setChoiceState(next), []);

  const value = useMemo(
    () => ({ choice, chosenOnThisDevice, theme, setChoice, adoptChoice }),
    [choice, chosenOnThisDevice, theme, setChoice, adoptChoice],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme() needs a <ThemeProvider> above it");
  return context;
}
